import React, { useState, useEffect, useMemo } from 'react';
import { 
  subscribeToUserCollection, 
  recordSale, 
  deleteUserDoc 
} from '../utils/userDataHelper';
import { num } from './utils/costCalculations';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/sales-analytics.css';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import useCurrencyTools from '../hooks/useCurrencyTools';

/**
 * SalesDashboard - Track sales, revenue, and business metrics
 * Route: /sales
 */

// Sale Card Component
const SaleCard = ({ sale, recipes, batches, onDelete, formatMoney }) => {
  const recipe = recipes.find(r => r.id === sale.recipeId);
  
  const profit = (num(sale.pricePerUnit, 0) - num(sale.costPerUnit, 0)) * num(sale.quantity, 1);
  const profitMargin = sale.pricePerUnit > 0 ? ((sale.pricePerUnit - sale.costPerUnit) / sale.pricePerUnit * 100) : 0;

  const marginClass = profitMargin > 40 ? 'high' : profitMargin > 20 ? 'medium' : 'low';
  const channelIcons = { online: 'Online', retail: 'Retail', direct: 'Direct', wholesale: 'Wholesale' };

  return (
    <div className="sal-card">
      <div className="sal-card-top">
        <div>
          <div className="sal-card-product">{sale.productName || recipe?.name || 'Unknown Product'}</div>
          <div className="sal-card-meta">
            {sale.customerName && <span>{sale.customerName} &bull; </span>}
            {new Date(sale.date).toLocaleDateString()}
          </div>
        </div>
        <div className="sal-card-amount">
          <div className="total">{formatMoney(num(sale.totalAmount, 0), 2)}</div>
          <div className={`margin ${marginClass}`}>{profitMargin.toFixed(1)}% margin</div>
        </div>
      </div>
      
      <div className="sal-card-stats">
        <div className="sal-stat-box">
          <div className="stat-label">Qty</div>
          <div className="stat-value">{sale.quantity}</div>
        </div>
        <div className="sal-stat-box">
          <div className="stat-label">Price</div>
          <div className="stat-value">{formatMoney(num(sale.pricePerUnit, 0), 0)}</div>
        </div>
        <div className="sal-stat-box">
          <div className="stat-label">Cost</div>
          <div className="stat-value">{formatMoney(num(sale.costPerUnit, 0), 0)}</div>
        </div>
        <div className="sal-stat-box profit-box">
          <div className="stat-label">Profit</div>
          <div className="stat-value">{formatMoney(profit, 0)}</div>
        </div>
      </div>

      {sale.notes && (
        <div className="sal-card-notes">{sale.notes}</div>
      )}

      {sale.channel && (
        <div>
          <span className={`sal-card-channel ${sale.channel}`}>
            {channelIcons[sale.channel] || sale.channel}
          </span>
        </div>
      )}

      <p className="bodytext">{sale.inventoryApplied === true ? 'Linked sale: finished batch stock was reduced.' : sale.inventoryStatus === 'unlinked' ? 'Accounting record only: no batch stock changed.' : 'Legacy sale: finished-stock effect unverified.'}</p>
      <div className="sal-card-actions">
        <button className="sal-btn-delete" onClick={() => onDelete(sale.id)}>
          Delete
        </button>
      </div>
    </div>
  );
};

// Add Sale Modal
const AddSaleModal = ({ recipes, batches, onClose, onAdd, formatMoney, currencySymbol, currencyCode }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [channel, setChannel] = useState('direct');
  const [notes, setNotes] = useState('');

  // Auto-fill when recipe selected
  useEffect(() => {
    if (selectedRecipe && !selectedBatch) {
      const recipe = recipes.find(r => r.id === selectedRecipe);
      if (recipe) {
        setProductName(recipe.name);
          const cost = recipe.totals?.perBar || recipe.costs?.totals?.perBar || 0;
          setCostPerUnit(cost);
          setPricePerUnit(recipe.pricing?.sellingPrice || recipe.costs?.pricing?.sellingPrice || (cost * 1.5));
      }
    }
  }, [selectedRecipe, selectedBatch, recipes]);

  // Auto-fill when batch selected
  useEffect(() => {
    if (selectedBatch) {
      const batch = batches.find(b => b.id === selectedBatch);
      if (batch) {
        if (batch.recipeId) setSelectedRecipe(batch.recipeId);
        setProductName(batch.recipeName || batch.name);
        setCostPerUnit(batch.costPerBar || 0);
      }
    }
  }, [selectedBatch, batches]);

  const totalAmount = num(quantity, 1) * num(pricePerUnit, 0);
  const totalProfit = num(quantity, 1) * (num(pricePerUnit, 0) - num(costPerUnit, 0));

  const handleSubmit = async () => {
    if (!productName || !quantity || !pricePerUnit) {
      alert('Please fill in product name, quantity, and price');
      return;
    }

    const sale = {
      recipeId: selectedRecipe || null,
      currencyCode,
      batchId: selectedBatch || null,
      productName,
      quantity: Number(quantity),
      pricePerUnit: Number(pricePerUnit),
      costPerUnit: Number(costPerUnit) || 0,
      totalAmount: totalAmount,
      profit: totalProfit,
      customerName: customerName || null,
      customerPhone: customerPhone || null,
      date,
      channel,
      notes: notes || null,
      createdAt: new Date().toISOString(),
    };

    setSaving(true); setError('');
    try { await onAdd(sale); onClose(); }
    catch (failure) { setError(failure.message || 'Failed to record sale.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="sal-modal-overlay" onClick={(e) => { if (!saving && e.target === e.currentTarget) onClose(); }}>
      <div className="sal-modal" role="dialog" aria-modal="true" aria-label="Record sale">
        <h3>Record Sale</h3>
        {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
        <p className="bodytext">Link a ready batch to deduct finished bars. A sale without a batch is an accounting record only.</p>

        <div className="sal-form-row two-col">
          <div className="sal-form-group">
            <label>Recipe (optional)</label>
            <select disabled={Boolean(selectedBatch)} value={selectedRecipe} onChange={(e) => setSelectedRecipe(e.target.value)}>
              <option value="">— Select recipe —</option>
              {recipes.map(r => (
                <option key={r.id} value={r.id}>{r.name || 'Untitled'}</option>
              ))}
            </select>
          </div>
          <div className="sal-form-group">
            <label>Batch (optional)</label>
            <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
              <option value="">— Select batch —</option>
              {batches.filter(b => ['ready', 'sold'].includes(b.status)).map(b => (
                <option key={b.id} value={b.id} disabled={b.remainingBars === undefined || b.remainingBars <= 0}>{b.name || 'Unnamed'} ({b.remainingBars === undefined ? 'legacy stock unverified' : `${b.remainingBars} bars remaining`})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="sal-form-row">
          <div className="sal-form-group">
            <label>Product Name *</label>
            <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g., Lavender Soap Bar" />
          </div>
        </div>

        <div className="sal-form-row three-col">
          <div className="sal-form-group">
            <label>Quantity *</label>
            <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="1" />
          </div>
          <div className="sal-form-group">
            <label>Selling Price ({currencySymbol}) *</label>
            <input type="number" value={pricePerUnit} onChange={(e) => setPricePerUnit(e.target.value)} placeholder="0" />
          </div>
          <div className="sal-form-group">
            <label>Cost ({currencySymbol}){selectedBatch ? ' · from batch' : ''}</label>
            <input type="number" disabled={Boolean(selectedBatch)} value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} placeholder="0" />
          </div>
        </div>

        {/* Total Preview */}
        <div className="sal-total-preview">
          <div className="row">
            <span>Total Amount:</span>
            <span className="total-amount">{formatMoney(totalAmount, 2)}</span>
          </div>
          <div className="row profit-amount">
            <span>Profit:</span>
            <span style={{ color: totalProfit > 0 ? '#16a34a' : '#dc2626' }}>{formatMoney(totalProfit, 2)}</span>
          </div>
        </div>

        <div className="sal-form-row two-col">
          <div className="sal-form-group">
            <label>Customer Name</label>
            <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Optional" />
          </div>
          <div className="sal-form-group">
            <label>Phone</label>
            <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div className="sal-form-row two-col">
          <div className="sal-form-group">
            <label>Sale Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="sal-form-group">
            <label>Sales Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="direct">Direct Sale</option>
              <option value="online">Online</option>
              <option value="retail">Retail</option>
              <option value="wholesale">Wholesale</option>
            </select>
          </div>
        </div>

        <div className="sal-form-row">
          <div className="sal-form-group">
            <label>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes about this sale..." rows={2} />
          </div>
        </div>

        <div className="sal-modal-actions">
          <button className="sal-btn-submit" disabled={saving} onClick={handleSubmit}>{saving ? 'Recording...' : 'Record Sale'}</button>
          <button className="sal-btn-cancel" disabled={saving} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// Main Sales Dashboard
function SalesDashboard() {
  const [sales, setSales] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [dateRange, setDateRange] = useState('month'); // 'week', 'month', 'year', 'all'
  const [filterChannel, setFilterChannel] = useState('all');
  const { currencyCode } = useAuth();
  const currency = currencyCode || 'INR';
  const { convertMoney } = useCurrencyTools();
  const currencySymbol = getCurrencySymbol(currency);
  const formatMoney = (value, fractionDigits = 0) =>
    formatCurrency(num(value, 0), currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  // Load data
  useEffect(() => {
    let unsubSales = () => {};
    let unsubRecipes = () => {};
    let unsubBatches = () => {};
    
    try {
      unsubSales = subscribeToUserCollection('sales', (data) => {
        // Sort by date descending
        const sorted = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSales(sorted);
        setLoading(false);
      });

      unsubRecipes = subscribeToUserCollection('recipes', (data) => {
        setRecipes(data);
      });

      unsubBatches = subscribeToUserCollection('batches', (data) => {
        setBatches(data);
      });
    } catch (err) {
      console.error('Error loading data:', err);
      setLoading(false);
    }

    return () => {
      unsubSales();
      unsubRecipes();
      unsubBatches();
    };
  }, []);

  // Filter sales by date range
  const filteredSales = useMemo(() => {
    const now = new Date();
    let startDate = new Date(0); // Beginning of time

    if (dateRange === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (dateRange === 'month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (dateRange === 'year') {
      startDate = new Date(now.getFullYear(), 0, 1);
    }

    return sales.filter(s => {
      const saleDate = new Date(s.date);
      const inDateRange = saleDate >= startDate;
      const inChannel = filterChannel === 'all' || s.channel === filterChannel;
      return inDateRange && inChannel;
    }).map(sale => ({ ...sale, ...Object.fromEntries(['pricePerUnit', 'costPerUnit', 'totalAmount', 'profit'].map(key => [key, convertMoney(num(sale[key], 0), sale.currencyCode || 'INR', currency)])) }));
  }, [sales, dateRange, filterChannel, convertMoney, currency]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const totalRevenue = filteredSales.reduce((sum, s) => sum + num(s.totalAmount, 0), 0);
    const totalProfit = filteredSales.reduce((sum, s) => sum + num(s.profit, 0), 0);
    const totalUnits = filteredSales.reduce((sum, s) => sum + num(s.quantity, 0), 0);
    const avgOrderValue = filteredSales.length > 0 ? totalRevenue / filteredSales.length : 0;
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    // Best sellers
    const productSales = {};
    filteredSales.forEach(s => {
      const name = s.productName || 'Unknown';
      if (!productSales[name]) productSales[name] = { name, quantity: 0, revenue: 0 };
      productSales[name].quantity += num(s.quantity, 0);
      productSales[name].revenue += num(s.totalAmount, 0);
    });
    const bestSellers = Object.values(productSales).sort((a, b) => b.quantity - a.quantity).slice(0, 5);

    // Sales by channel
    const channelSales = {};
    filteredSales.forEach(s => {
      const ch = s.channel || 'direct';
      if (!channelSales[ch]) channelSales[ch] = { channel: ch, count: 0, revenue: 0 };
      channelSales[ch].count++;
      channelSales[ch].revenue += num(s.totalAmount, 0);
    });

    // Customer stats
    const uniqueCustomers = new Set(filteredSales.filter(s => s.customerName).map(s => s.customerName)).size;

    return {
      totalRevenue,
      totalProfit,
      totalUnits,
      avgOrderValue,
      profitMargin,
      bestSellers,
      channelSales: Object.values(channelSales),
      uniqueCustomers,
      totalOrders: filteredSales.length,
    };
  }, [filteredSales]);

  // Add sale
  const handleAddSale = sale => recordSale(sale);

  // Delete sale
  const handleDeleteSale = async (id) => {
    const sale = sales.find(item => item.id === id);
    if (!window.confirm(sale?.inventoryApplied === true ? 'Delete this sale and return its bars to the linked batch?' : 'Delete this sale record? Its stock effect is unverified or unlinked, so batch stock will not change.')) return;
    try {
      await deleteUserDoc('sales', id);
    } catch (err) {
      console.error('Failed to delete sale:', err);
      alert('Failed to delete sale: ' + err.message);
    }
  };

  if (loading) {
    return (
      <>
        <div className="sl-page sales-page">
          <div className="pageheader borderleft">
            <Breadcrumbs items={[
              { label: 'Home', to: '/' },
              { label: 'Sales' }
            ]} />
            <div className="pageheading">Sales Dashboard</div>
            <div className="pagedescription">Loading sales data...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="sl-page sales-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Sales' }
          ]} />
          <div className="pageheading">Sales Dashboard</div>
          <div className="pagedescription">Track revenue, profits, and best sellers.</div>
        </div>

        <div className="sales-page-content borderleft">
          {/* Toolbar: Filters + Record Sale */}
          <section className="mypadding borderbottom">
            <div className="sal-toolbar">
              <div className="sal-toolbar-filters">
                <select className="sal-select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                </select>
                <select className="sal-select" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
                  <option value="all">All Channels</option>
                  <option value="direct">Direct</option>
                  <option value="online">Online</option>
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                </select>
              </div>
              <button className="sal-btn-record" onClick={() => setShowAddModal(true)}>
                Record Sale
              </button>
            </div>
          </section>

          {/* Metrics */}
          <section className="mypadding borderbottom">
            <div className="sal-metrics">
              <div className="sal-metric-card revenue">
                <div className="label">Total Revenue</div>
                <div className="value">{formatMoney(metrics.totalRevenue, 0)}</div>
              </div>
              <div className="sal-metric-card profit">
                <div className="label">Total Profit</div>
                <div className="value">{formatMoney(metrics.totalProfit, 0)}</div>
              </div>
              <div className="sal-metric-card units">
                <div className="label">Units Sold</div>
                <div className="value">{metrics.totalUnits}</div>
              </div>
              <div className="sal-metric-card avg">
                <div className="label">Avg Order Value</div>
                <div className="value">{formatMoney(metrics.avgOrderValue, 0)}</div>
              </div>
              <div className="sal-metric-card margin">
                <div className="label">Profit Margin</div>
                <div className="value">{metrics.profitMargin.toFixed(1)}%</div>
              </div>
              <div className="sal-metric-card customers">
                <div className="label">Customers</div>
                <div className="value">{metrics.uniqueCustomers}</div>
              </div>
            </div>
          </section>

          {/* Main content: Sales list + sidebar */}
          <section className="mypadding">
            <div className="sal-main-grid">
              {/* Sales list */}
              <div>
                <h3 style={{ margin: '0 0 16px', fontSize: '1.05em' }}>Recent Sales ({filteredSales.length})</h3>
                
                {filteredSales.length === 0 ? (
                  <div className="sal-empty">
                    <div className="sal-empty-text">No sales recorded yet</div>
                    <button className="sal-empty-btn" onClick={() => setShowAddModal(true)}>
                      Record Your First Sale
                    </button>
                  </div>
                ) : (
                  <div>
                    {filteredSales.map(sale => (
                      <SaleCard
                        key={sale.id}
                        sale={sale}
                        recipes={recipes}
                        batches={batches}
                        onDelete={handleDeleteSale}
                        formatMoney={formatMoney}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div>
                {/* Best Sellers */}
                <div className="sal-sidebar-card">
                  <h4>Best Sellers</h4>
                  {metrics.bestSellers.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: '0.88em' }}>No data yet</div>
                  ) : (
                    metrics.bestSellers.map((p, i) => (
                      <div key={p.name} className="sal-bestseller-row">
                        <div className="sal-bestseller-info">
                          <span className={`sal-bestseller-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'plain'}`}>
                            {i + 1}
                          </span>
                          <span>{p.name}</span>
                        </div>
                          <div className="sal-bestseller-stats">
                          <div className="qty">{p.quantity} sold</div>
                          <div className="rev">{formatMoney(p.revenue, 0)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Sales by Channel */}
                <div className="sal-sidebar-card">
                  <h4>Sales by Channel</h4>
                  {metrics.channelSales.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: '0.88em' }}>No data yet</div>
                  ) : (
                    metrics.channelSales.map(ch => {
                      const colors = { direct: '#dc2626', online: '#2563eb', retail: '#9333ea', wholesale: '#ea580c' };
                      const pct = metrics.totalRevenue > 0 ? (ch.revenue / metrics.totalRevenue) * 100 : 0;
                      
                      return (
                        <div key={ch.channel} className="sal-channel-row">
                          <div className="sal-channel-label">
                            <span className="name">{ch.channel}</span>
                            <span className="info">{formatMoney(ch.revenue, 0)} ({ch.count})</span>
                          </div>
                          <div className="sal-channel-bar">
                            <div className="fill" style={{ width: `${pct}%`, background: colors[ch.channel] || '#2563eb' }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>

      {/* Add Sale Modal */}
      {showAddModal && (
        <AddSaleModal
        currencyCode={currency}
        recipes={recipes}
        batches={batches}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddSale}
        formatMoney={formatMoney}
        currencySymbol={currencySymbol}
        />
      )}

      <Footer />
      </div>
    </>
  );
}

export default SalesDashboard;
